import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Select API",
  "title": "Select",
  "props": ["SelectProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const LeadParagraph = makeShortcode("LeadParagraph");
const Link = makeShortcode("Link");
const Alert = makeShortcode("Alert");
const SelectProps = makeShortcode("SelectProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <PageContent componentName="select" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Select components are used for collecting user provided information from a
  list of options.
      </LeadParagraph>
      <p>{`For combobox/autocomplete functionality, please see the `}<Link to="/api/combobox" mdxType="Link">{`Combobox component`}</Link>{`.`}</p>
      <p>{`If you are migrating from the Legacy Select, please see the `}<Link to="/select-migration" mdxType="Link">{`Select Migration docs`}</Link>{` for details on how to make the transition.`}</p>
      <h2>{`Basic Usage`}</h2>
      <p>{`By default when passing in a `}<inlineCode parentName="p">{`selectedItem`}</inlineCode>{` it will be checked to make sure it is in your `}<inlineCode parentName="p">{`items`}</inlineCode>{` array. The check will use the `}<inlineCode parentName="p">{`itemToString`}</inlineCode>{` function on both the `}<inlineCode parentName="p">{`selectedItem`}</inlineCode>{` and each `}<inlineCode parentName="p">{`item`}</inlineCode>{` in the array. The default `}<inlineCode parentName="p">{`itemToString`}</inlineCode>{` function is able to be used for `}<inlineCode parentName="p">{`items`}</inlineCode>{` that are either `}<inlineCode parentName="p">{`strings`}</inlineCode>{` or an object with the shape `}<inlineCode parentName="p">{`{label: string; value: string;}`}</inlineCode>{`.`}</p>
      <p>{`If you are using a custom items format see the `}<a parentName="p" {...{
          "href": "/version/4.9.1/api/select/#custom_items"
        }}>{`Custom Items example`}</a>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  const [selectedItem, updateSelectedItem] = React.useState('');

  function onSelectedItemChange(changes) {
    updateSelectedItem(changes.selectedItem);
  }

  return (
    <>
      <Select
        id="basicSelectId"
        labelText="Basic"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        selectedItem={selectedItem}
        onSelectedItemChange={onSelectedItemChange}
      />

      <Select
        id="badSelectedItemId"
        labelText="Bad selected item"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        selectedItem={{ label: 'Purple', value: 'purple' }}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Initial State`}</h2>
      <p>{`Set initial state using the `}<inlineCode parentName="p">{`initialSelectedItem`}</inlineCode>{`, `}<inlineCode parentName="p">{`initialSelectedItems`}</inlineCode>{`, and `}<inlineCode parentName="p">{`initialHighlightedIndex`}</inlineCode>{` props.`}</p>
      <Alert variant="info" mdxType="Alert">
  When using any of the <inlineCode className="inverse">initial*</inlineCode>{' '}
  props be aware that passing in the controlled version of that prop will
  overwrite the initial version.
  <br />
  <br />
  <strong>Ex: </strong>
  <inlineCode className="inverse">selectedItem</inlineCode> takes precedence
  over <inlineCode className="inverse">initialSelectedItem</inlineCode>
      </Alert>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Select
        id="initialSelectedByItemSelectId"
        labelText="Initial selected by item"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        initialSelectedItem={{ label: 'Green', value: 'green' }}
      />

      <Select
        id="initialHighlightedIndexSelectId"
        labelText="Initial highlighted index"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        initialHighlightedIndex={1}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Label Position`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`labelPosition`}</inlineCode>{` prop can be used to set the position of the text label relative to the form field. It accepts either `}<inlineCode parentName="p">{`top`}</inlineCode>{` or `}<inlineCode parentName="p">{`left`}</inlineCode>{`, with `}<inlineCode parentName="p">{`top`}</inlineCode>{` as the default.`}</p>
      <p>{`Left-aligned lables are not recommended for use in standard forms; instead they are designed for smaller spaces where vertical space is limited, such as in a toolbar.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select, LabelPosition } from 'react-magma-dom';

export function Example() {
  return (
    <Select
      labelPosition={LabelPosition.left}
      labelText="Left-aligned label"
      items={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
    />
  );
}
`}</code></pre>
      <h2>{`Disabled`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  return (
    <Select
      id="disabledSelectId"
      disabled
      labelText="Disabled"
      items={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
    />
  );
}
`}</code></pre>
      <h2>{`Disabled Items`}</h2>
      <p>{`To disable specific items in the `}<inlineCode parentName="p">{`Select`}</inlineCode>{` component, add a `}<inlineCode parentName="p">{`disabled: true`}</inlineCode>{` property to each item object. Disabled items specified as `}<inlineCode parentName="p">{`defaultSelectedItem`}</inlineCode>{` or `}<inlineCode parentName="p">{`initialSelectedItem`}</inlineCode>{` will not be selected by default or initially. If `}<inlineCode parentName="p">{`initialHighlightedIndex`}</inlineCode>{` points to a `}<inlineCode parentName="p">{`disabled`}</inlineCode>{` item, the component will not highlight it.`}</p>
      <p>{`Example:`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Select
        id="disabledItemsSelectId"
        labelText="Select with Disabled Items"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue', disabled: true },
          { label: 'Green', value: 'green' },
        ]}
        isClearable
        initialHighlightedIndex={1}
        defaultSelectedItem={{ label: 'Blue', value: 'blue', disabled: true }}
        initialSelectedItem={{ label: 'Blue', value: 'blue', disabled: true }}
      />
      <br />
      <Select
        id="disabledItemsSelectId"
        labelText="Multi Select with Disabled Items"
        isMulti
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Green', value: 'green' },
          { label: 'Blue-Disabled', value: 'blue', disabled: true },
          { label: 'Purple mountain majesty', value: 'purple' },
          { label: 'Orange', value: 'orange', disabled: false },
          {
            label: 'Yellow-Disabled',
            value: 'Yellow-Disabled',
            disabled: true,
          },
        ]}
        isClearable
        initialHighlightedIndex={2}
        defaultSelectedItems={[
          { label: 'Red', value: 'red' },
          { label: 'Orange', value: 'orange', disabled: false },
          {
            label: 'Yellow-Disabled',
            value: 'Yellow-Disabled',
            disabled: true,
          },
        ]}
        initialSelectedItems={[
          { label: 'Red', value: 'red' },
          { label: 'Orange', value: 'orange', disabled: false },
          {
            label: 'Yellow-Disabled',
            value: 'Yellow-Disabled',
            disabled: true,
          },
        ]}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Clearable`}</h2>
      <p>{`The optional `}<inlineCode parentName="p">{`isClearable`}</inlineCode>{` prop allows the user to clear the field once a selection has been made.`}</p>
      <p>{`When using the `}<inlineCode parentName="p">{`isClearable`}</inlineCode>{` prop you can choose a default selected item to be set once the `}<inlineCode parentName="p">{`select`}</inlineCode>{` is cleared
using the `}<inlineCode parentName="p">{`defaultSelectedItem`}</inlineCode>{` prop.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Select
        id="clearableSelectId"
        labelText="Clearable"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        initialSelectedItem={{ label: 'Green', value: 'green' }}
        isClearable
      />

      <Select
        id="clearableWithDefaultsId"
        labelText="Clearable with defaults"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        isClearable
        defaultSelectedItem={{ label: 'Blue', value: 'blue' }}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Error Message`}</h2>
      <p>{`If a select has an `}<inlineCode parentName="p">{`errorMessage`}</inlineCode>{`, the select will be styled to highlight its error state and the error message will appear below the field.
If an error message is present, it will replace the helper text. Can be a node or a string.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Select
        id="errorMessage"
        labelText="Error message"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        errorMessage="Please select a color"
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Helper Message`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`helperMessage`}</inlineCode>{` appears underneath the select field. It will not appear if an error message is present. Can be a node or a string.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Select
        id="helperMessage"
        labelText="Helper message"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        helperMessage="Helper text goes here"
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Placeholder`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`placeholder`}</inlineCode>{` prop can be used to replace the default text shown before an item is chosen.
Placeholder text should be used to provide supplemental information about the input field. It should not be
relied upon to take the place of the label text.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  return (
    <Select
      id="placeholderSelect"
      labelText="Placeholder example"
      items={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
      placeholder="Choose a color"
    />
  );
}
`}</code></pre>
      <h2>{`Multi Select`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  const [selectedItems, updateSelectedItems] = React.useState([]);

  function handleSelectedItemsChange(changes) {
    updateSelectedItems(changes.selectedItems);
  }

  function handleRemoveSelectedItem(removedItem) {
    updateSelectedItems(selectedItems.filter(item => item !== removedItem));
  }

  return (
    <>
      <Select
        id="multiSelectId"
        isMulti
        labelText="Multi select"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
          { label: 'Orange', value: 'orange' },
          { label: 'Aqua', value: 'aqua' },
          { label: 'Gold', value: 'gold' },
          { label: 'Periwinkle', value: 'periwinkle' },
          { label: 'Lavender', value: 'lavender' },
          { label: 'Marigold', value: 'marigold' },
          { label: 'Yellow', value: 'yellow' },
          { label: 'Purple', value: 'purple' },
          { label: 'Dusty Rose', value: 'dusty_rose' },
          { label: 'Burnt Sienna', value: 'burnt_sienna' },
        ]}
        initialSelectedItems={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Blah', value: 'blah' },
        ]}
      />

      <Select
        id="multiSelectControlledId"
        isMulti
        labelText="Multi select controlled"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
          { label: 'Orange', value: 'orange' },
          { label: 'Aqua', value: 'aqua' },
          { label: 'Gold', value: 'gold' },
          { label: 'Periwinkle', value: 'periwinkle' },
          { label: 'Lavender', value: 'lavender' },
          { label: 'Marigold', value: 'marigold' },
          { label: 'Yellow', value: 'yellow' },
          { label: 'Purple', value: 'purple' },
          { label: 'Dusty Rose', value: 'dusty_rose' },
          { label: 'Burnt Sienna', value: 'burnt_sienna' },
        ]}
        selectedItems={selectedItems}
        onSelectedItemsChange={handleSelectedItemsChange}
        onRemoveSelectedItem={handleRemoveSelectedItem}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Help Link`}</h2>
      <p>{`When a select needs additional context related to the user, `}<inlineCode parentName="p">{`additionalContent`}</inlineCode>{` allows child elements to help. It is encouraged to use this prop with a `}<inlineCode parentName="p">{`Tooltip`}</inlineCode>{` wrapping an `}<inlineCode parentName="p">{`IconButton`}</inlineCode>{`.`}</p>
      <p>{`By default, the child element will display inline with a `}<inlineCode parentName="p">{`Select`}</inlineCode>{` label on the right.`}</p>
      <p>{`When `}<inlineCode parentName="p">{`labelPosition`}</inlineCode>{` is set to `}<inlineCode parentName="p">{`left`}</inlineCode>{`, the label, select, and child elements all display inline.`}</p>
      <p>{`If no label is required, use `}<inlineCode parentName="p">{`isLabelVisuallyHidden`}</inlineCode>{` and the same inline layout will persist.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import {
  Select,
  LabelPosition,
  Tooltip,
  IconButton,
  ButtonSize,
  ButtonType,
  ButtonVariant,
} from 'react-magma-dom';
import { HelpIcon } from 'react-magma-icons';

export function Example() {
  const helpLinkLabel = 'Learn more';
  const onHelpLinkClick = () => {
    alert('Help link clicked!');
  };

  return (
    <>
      <Select
        additionalContent={
          <Tooltip content={helpLinkLabel}>
            <IconButton
              aria-label={helpLinkLabel}
              icon={<HelpIcon />}
              onClick={onHelpLinkClick}
              type={ButtonType.button}
              size={ButtonSize.small}
              variant={ButtonVariant.link}
            />
          </Tooltip>
        }
        labelText="Default positioning"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
      />
      <br />
      <Select
        additionalContent={
          <Tooltip content={helpLinkLabel}>
            <IconButton
              aria-label={helpLinkLabel}
              icon={<HelpIcon />}
              onClick={onHelpLinkClick}
              type={ButtonType.button}
              size={ButtonSize.small}
              variant={ButtonVariant.link}
            />
          </Tooltip>
        }
        labelPosition={LabelPosition.left}
        labelText="Left positioning"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select, Card, CardBody } from 'react-magma-dom';

export function Example() {
  const [selectedItem, updateSelectedItem] = React.useState('');

  function onSelectedItemChange(changes) {
    updateSelectedItem(changes.selectedItem);
  }

  return (
    <Card isInverse>
      <CardBody>
        <Select
          id="basicSelectInverseId"
          isInverse
          labelText="Basic"
          items={[
            { label: 'Red', value: 'red' },
            { label: 'Blue', value: 'blue' },
            { label: 'Green', value: 'green' },
          ]}
          selectedItem={selectedItem}
          onSelectedItemChange={onSelectedItemChange}
        />

        <Select
          id="multiSelectInverseId"
          isInverse
          isMulti
          labelText="Multi select"
          items={[
            { label: 'Red', value: 'red' },
            { label: 'Blue', value: 'blue' },
            { label: 'Green', value: 'green' },
          ]}
        />
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Specific State Changes`}</h2>
      <p>{`You can track specific changes made to the internal state of the `}<inlineCode parentName="p">{`select`}</inlineCode>{` using the following functions: `}<inlineCode parentName="p">{`onHighlightedIndexChange`}</inlineCode>{`,
`}<inlineCode parentName="p">{`onIsOpenChange`}</inlineCode>{`, and `}<inlineCode parentName="p">{`onSelectedItemChange`}</inlineCode>{`. Each of these functions will have a `}<inlineCode parentName="p">{`changes`}</inlineCode>{` object as a parameter that
includes the changes made to the corresponding state from each action.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  const [highlightedIndexChangeCount, updateHighlightedIndexChangeCount] =
    React.useState(0);
  const [isOpenChangeCount, updateIsOpenChangeCount] = React.useState(0);
  const [selectedItem, updateSelectedItem] = React.useState(undefined);

  function onHighlightedIndexChange(changes) {
    updateHighlightedIndexChangeCount(highlightedIndexChangeCount + 1);
  }

  function onIsOpenChange(changes) {
    updateIsOpenChangeCount(isOpenChangeCount + 1);
  }

  function onSelectedItemChange(changes) {
    updateSelectedItem(changes.selectedItem);
  }

  return (
    <>
      <strong>Selected Item: </strong>
      <pre>{JSON.stringify(selectedItem, null, 2)}</pre>
      <p>
        <strong>Highlighted Index Change Count: </strong>
        {highlightedIndexChangeCount}
      </p>
      <p>
        <strong>Is Open Change Count: </strong>
        {isOpenChangeCount}
      </p>

      <Select
        id="stateChangesId1"
        labelText="State changes"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        onHighlightedIndexChange={onHighlightedIndexChange}
        onIsOpenChange={onIsOpenChange}
        onSelectedItemChange={onSelectedItemChange}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Generic State Change`}</h2>
      <Alert variant="warning" mdxType="Alert">
  Using <inlineCode>onStateChange</inlineCode> and controlling every piece of{' '}
  <inlineCode>state</inlineCode> will result in losing most of the functionality
  that has been built in to this component. Only use this feature as a last
  resort.
      </Alert>
      <p><inlineCode parentName="p">{`onStateChange`}</inlineCode>{` is called when any internal state is changed. Therefore, it is best used when using the
`}<inlineCode parentName="p">{`select`}</inlineCode>{` in a controlled state.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`onStateChange`}</inlineCode>{` function passes a `}<inlineCode parentName="p">{`changes`}</inlineCode>{` object that includes properties that have changed since the
last state change. The `}<inlineCode parentName="p">{`changes`}</inlineCode>{` object also includes a `}<inlineCode parentName="p">{`type`}</inlineCode>{` property that describes the action taken
to change the state. You can see the full list of the types in the `}<a parentName="p" {...{
          "href": "/version/4.9.1/api/select/#state_change_types"
        }}><inlineCode parentName="a">{`stateChangeTypes`}</inlineCode></a>{` section.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  const [changes, updateChanges] = React.useState({});
  const [highlightedIndex, updateHighlightedIndex] = React.useState(-1);
  const [isOpen, updateIsOpen] = React.useState(false);
  const [selectedItem, updateSelectedItem] = React.useState('');

  function onStateChange(newChanges) {
    const { type } = newChanges;
    updateChanges(newChanges);

    switch (type) {
      case SelectStateChangeTypes.ToggleButtonClick:
        updateIsOpen(!isOpen);
        break;
      case SelectStateChangeTypes.ToggleButtonKeyDownArrowDown:
        updateIsOpen(newChanges.isOpen);
        updateHighlightedIndex(newChanges.highlightedIndex);
        break;
      case SelectStateChangeTypes.ItemMouseMove:
      case SelectStateChangeTypes.FunctionSetHighlightedIndex:
      case SelectStateChangeTypes.MenuKeyDownArrowDown:
      case SelectStateChangeTypes.MenuKeyDownArrowUp:
        updateHighlightedIndex(newChanges.highlightedIndex);
        break;
      case SelectStateChangeTypes.MenuKeyDownEscape:
      case SelectStateChangeTypes.MenuBlur:
        updateIsOpen(false);
        updateHighlightedIndex(newChanges.highlightedIndex);
        break;
      case SelectStateChangeTypes.ItemClick:
        updateHighlightedIndex(newChanges.highlightedIndex);
        updateIsOpen(newChanges.isOpen);
        updateSelectedItem(newChanges.selectedItem);
        break;
      default:
        break;
    }
  }

  return (
    <>
      <pre>{JSON.stringify(changes, null, 2)}</pre>

      <Select
        id="stateChangesId2"
        labelText="State changes"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        onStateChange={onStateChange}
        highlightedIndex={highlightedIndex}
        isOpen={isOpen}
        selectedItem={selectedItem}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Controlled`}</h2>
      <p>{`If you wish to control the state rather than having it handled internally you can use the control props
provided. These props are `}<inlineCode parentName="p">{`isOpen`}</inlineCode>{`, `}<inlineCode parentName="p">{`selectedItem`}</inlineCode>{`, and `}<inlineCode parentName="p">{`highlightedIndex`}</inlineCode>{`. If you are using these control props
you will have to use the handlers mentioned above in the `}<a parentName="p" {...{
          "href": "/version/4.9.1/api/select/#state_change_types"
        }}>{`State Changes`}</a>{` section.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  const [highlightedIndex, updateHighlightedIndex] = React.useState(0);
  const [isOpen, updateIsOpen] = React.useState(false);
  const [selectedItem, updateSelectedItem] = React.useState('');

  function onHighlightedIndexChange(changes) {
    updateHighlightedIndex(changes.highlightedIndex);
  }

  function onIsOpenChange(changes) {
    updateIsOpen(changes.isOpen);
  }

  function onSelectedItemChange(changes) {
    updateSelectedItem(changes.selectedItem);
  }

  return (
    <>
      <strong>Selected Item: </strong>
      <pre>{JSON.stringify(selectedItem, null, 2)}</pre>
      <p>
        <strong>Highlighted Index: </strong>
        {highlightedIndex}
      </p>
      <p>
        <strong>Is Open: </strong>
        {isOpen.toString()}
      </p>

      <Select
        id="controlledId"
        labelText="Controlled"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        isOpen={isOpen}
        highlightedIndex={highlightedIndex}
        selectedItem={selectedItem}
        onHighlightedIndexChange={onHighlightedIndexChange}
        onIsOpenChange={onIsOpenChange}
        onSelectedItemChange={onSelectedItemChange}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Accessibility`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`getA11yStatusMessage`}</inlineCode>{` prop is a passed in function that allows for customization of the screen-reader accessible message whenever the following props
are changed: `}<inlineCode parentName="p">{`items`}</inlineCode>{`, `}<inlineCode parentName="p">{`highlightedIndex`}</inlineCode>{`, `}<inlineCode parentName="p">{`inputValue`}</inlineCode>{` or `}<inlineCode parentName="p">{`isOpen`}</inlineCode>{`.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`getA11ySelectionMessage`}</inlineCode>{` prop is a passed in function that allows for customization of the screen-reader accessible message
whenever an item has been selected.`}</p>
      <p>{`For both the `}<inlineCode parentName="p">{`getA11yStatusMessage`}</inlineCode>{` and `}<inlineCode parentName="p">{`getA11ySelectionMessage`}</inlineCode>{` functions there is an
`}<a parentName="p" {...{
          "href": "https://github.com/downshift-js/downshift/tree/master/src/hooks/useSelect#geta11yselectionmessage"
        }}>{`object passed with internal state data`}</a>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Select
        id="newA11yStatusMessageId"
        labelText="New accessibility status message"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        getA11yStatusMessage={({ highlightedItem }) =>
          \`custom message saying that \${highlightedItem} is highlighted\`
        }
        getA11ySelectionMessage={({ selectedItem }) =>
          \`custom message saying that \${selectedItem} is now selected\`
        }
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Internationalization`}</h2>
      <p>{`Some of the internationalization overrides use placeholders to insert selected values in to the message.
Placeholders are specific keywords surrounded by curly braces.`}</p>
      <ul>
        <li parentName="ul"><inlineCode parentName="li">{`{labelText}`}</inlineCode>{` will be replaced with the comboboxes `}<inlineCode parentName="li">{`labelText`}</inlineCode>{`.`}</li>
        <li parentName="ul"><inlineCode parentName="li">{`{selectedItem}`}</inlineCode>{` will be replaced by the current `}<inlineCode parentName="li">{`itemToString`}</inlineCode>{` representation of the `}<inlineCode parentName="li">{`selectedItem`}</inlineCode>{` of the `}<inlineCode parentName="li">{`select`}</inlineCode>{`.`}</li>
      </ul>
      <p>{`Full example of `}<Link to="/api/internationalization" mdxType="Link">{`internationalization override options`}</Link></p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select, I18nContext, defaultI18n } from 'react-magma-dom';

export function Example() {
  return (
    <I18nContext.Provider
      value={{
        ...defaultI18n,
        select: {
          ...defaultI18n.select,
          placeholder: 'Custom Select Placeholder...',
          clearIndicatorAriaLabel:
            'Click to reset selection for {labelText}. {selectedItem} is currently selected',
        },
        multiSelect: {
          ...defaultI18n.multiSelect,
          selectedItemButtonAriaLabel:
            'Click to reset the selected item {selectedItem}',
        },
      }}
    >
      <Select
        labelText="Internationalization"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        initialSelectedItem={{ label: 'Red', value: 'red' }}
        isClearable
      />

      <Select
        labelText="Multi internationalization"
        isMulti
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        initialSelectedItems={[{ label: 'Red', value: 'red' }]}
      />
    </I18nContext.Provider>
  );
}
`}</code></pre>
      <h2>{`Events`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  const [currentSelectEvent, updateCurrentSelectEvent] =
    React.useState(undefined);

  function handleSelectBlur(event) {
    updateCurrentSelectEvent('Blur');
  }

  function handleSelectFocus(event) {
    updateCurrentSelectEvent('Focus');
  }

  function handleSelectKeyPress(event) {
    updateCurrentSelectEvent('KeyPress');
  }

  return (
    <>
      <p>
        <strong>{currentSelectEvent || 'No'} event was triggered</strong>
      </p>

      <Select
        id="selectFocusEventId"
        labelText="Select focus events"
        items={[
          { label: 'Red', value: 'red' },
          { label: 'Blue', value: 'blue' },
          { label: 'Green', value: 'green' },
        ]}
        onBlur={handleSelectBlur}
        onFocus={handleSelectFocus}
        onKeyPress={handleSelectKeyPress}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Custom Items List Height`}</h2>
      <p>{`The items list menu has a default max height that can be changed in the theme or using the `}<inlineCode parentName="p">{`itemListMaxHeight`}</inlineCode>{` prop.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  return (
    <Select
      labelText="Select small item list menu"
      items={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
      itemListMaxHeight="50px"
    />
  );
}
`}</code></pre>
      <h2>{`Custom Items`}</h2>
      <p>{`By default each component accepts an array of `}<inlineCode parentName="p">{`items`}</inlineCode>{` with either a `}<inlineCode parentName="p">{`string`}</inlineCode>{` or an `}<inlineCode parentName="p">{`object`}</inlineCode>{` with the shape `}<inlineCode parentName="p">{`{item: string; label: string;}`}</inlineCode>{`
for each `}<inlineCode parentName="p">{`item`}</inlineCode>{`. If you need to pass in a custom shape for your `}<inlineCode parentName="p">{`items`}</inlineCode>{` you can pass in an additional prop named `}<inlineCode parentName="p">{`itemToString`}</inlineCode>{`
which is a function that returns the string representation of your `}<inlineCode parentName="p">{`item`}</inlineCode>{` which will be applied as the label.`}</p>
      <Alert variant="warning" mdxType="Alert">
  <strong>WARNING</strong> Your function must include a{' '}
  <inlineCode>null</inlineCode> check.
      </Alert>
      <p>{`If you are using a custom shape for your `}<inlineCode parentName="p">{`items`}</inlineCode>{` in a `}<inlineCode parentName="p">{`typescript`}</inlineCode>{` project refer to our example of `}<a parentName="p" {...{
          "href": "#custom_items_typescript"
        }}>{`using your
custom `}<inlineCode parentName="a">{`type`}</inlineCode></a>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  function itemToString(item) {
    return item ? \`\${item.representation} (\${item.hex})\` : '';
  }

  return (
    <>
      <Select
        id="itemToStringId"
        labelText="Item to string"
        items={[
          {
            id: 1,
            actual: 'red',
            representation: 'Red',
            hex: '#FF0000',
          },
          {
            id: 2,
            actual: 'blue',
            representation: 'Blue',
            hex: '#0000FF',
          },
          {
            id: 3,
            actual: 'green',
            representation: 'Green',
            hex: '#008000',
          },
        ]}
        itemToString={itemToString}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Custom Items Typescript`}</h2>
      <p>{`When using a custom shape for your `}<inlineCode parentName="p">{`items`}</inlineCode>{` in a `}<inlineCode parentName="p">{`typescript`}</inlineCode>{` project you will need to provide the `}<inlineCode parentName="p">{`Select`}</inlineCode>{`
element with the shape of the `}<inlineCode parentName="p">{`items`}</inlineCode>{` you are passing in.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  interface CustomSelectItem {
    id: number;
    actual: string;
    representation: string;
    hex?: string;
  }

  const customItems: CustomSelectItem[] = [
    {
      id: 1,
      actual: 'red',
      representation: 'Red',
      hex: '#FF0000',
    },
    {
      id: 2,
      actual: 'blue',
      representation: 'Blue',
      hex: '#0000FF',
    },
    {
      id: 3,
      actual: 'green',
      representation: 'Green',
      hex: '#008000',
    },
  ];

  function itemToString(item: CustomSelectItem) {
    return item ? \`\${item.representation} (\${item.hex})\` : '';
  }

  return (
    <>
      <Select<CustomSelectItem>
        id="customItemToStringTypescriptId"
        labelText="Custom items with typescript"
        items={customItems}
        itemToString={itemToString}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Custom Styles`}</h2>
      <p>{`Custom styles can be passed into the Select component. The `}<inlineCode parentName="p">{`containerStyle`}</inlineCode>{` property will apply to the container.
Additional `}<inlineCode parentName="p">{`labelStyle`}</inlineCode>{`, `}<inlineCode parentName="p">{`inputStyle`}</inlineCode>{` and `}<inlineCode parentName="p">{`messageStyle`}</inlineCode>{` properties are available to style the respective elements.
Please use discretion when adding custom styles.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  const [selectedItem, updateSelectedItem] = React.useState('');

  function onSelectedItemChange(changes) {
    updateSelectedItem(changes.selectedItem);
  }

  return (
    <>
      <Select
        containerStyle={{ marginBottom: '32px' }}
        helperMessage="Helper message"
        inputStyle={{ border: '2px dotted green', width: '200px' }}
        items={['Red', 'Blue', 'Green']}
        labelStyle={{ fontStyle: 'italic' }}
        labelText="Basic with custom styles"
        messageStyle={{ border: '1px solid blue' }}
        selectedItem={selectedItem}
        onSelectedItemChange={onSelectedItemChange}
      />

      <Select
        containerStyle={{ marginBottom: '32px' }}
        helperMessage="Helper message"
        inputStyle={{ border: '2px dotted green', width: '200px' }}
        isMulti
        items={['Red', 'Blue', 'Green']}
        labelStyle={{ fontStyle: 'italic' }}
        labelText="Multi with custom styles"
        messageStyle={{ border: '1px solid blue' }}
        selectedItem={selectedItem}
        onSelectedItemChange={onSelectedItemChange}
      />
    </>
  );
}
`}</code></pre>
      <h2>{`Custom Item Component`}</h2>
      <p>{`By default each item will be rendered with the `}<inlineCode parentName="p">{`itemToString`}</inlineCode>{` value in a styled `}<inlineCode parentName="p">{`li`}</inlineCode>{`. If you would like to add more information or
would just like to be able to fully customize the look of your items you can pass in an `}<inlineCode parentName="p">{`Item`}</inlineCode>{` component that uses the `}<inlineCode parentName="p">{`props`}</inlineCode>{` that
we use internally.`}</p>
      <Alert variant="warning" mdxType="Alert">
  Be aware. You must pass a <inlineCode>ref</inlineCode> to your custom item
  component to make sure the functionality of the entire{' '}
  <inlineCode>Select</inlineCode> continues to work.
      </Alert>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import styled from '@emotion/styled';
import { Select } from 'react-magma-dom';

export function Example() {
  const CustomStyledItem = styled.li(props => ({
    alignSelf: 'center',
    background: props.isFocused ? props.theme.colors.neutral200 : 'transparent',
    lineHeight: '24px',
    margin: '0',
    padding: '8px 16px',
  }));

  const ContainerSpan = styled.span(props => ({
    display: 'flex',
    alignItems: 'center',
  }));

  const Hex = styled.span(props => ({
    background: props.color,
    border: \`2px solid \${props.theme.colors.neutral}\`,
    borderRadius: props.theme.borderRadius,
    display: 'inline-flex',
    height: '16px',
    marginRight: '4px',
    width: '16px',
  }));

  const Description = styled.div(props => ({
    fontSize: '12px',
    color: props.theme.colors.neutral,
  }));

  function itemToString(item) {
    return item ? item.representation : '';
  }

  const CustomItem = props => {
    const { itemRef, itemString, item, ...other } = props;
    return (
      <CustomStyledItem {...other} ref={itemRef}>
        <ContainerSpan>
          <Hex color={item.hex} theme={props.theme} />
          {itemString}
        </ContainerSpan>
        <Description theme={props.theme}>{item.description}</Description>
      </CustomStyledItem>
    );
  };

  return (
    <Select
      components={{ Item: CustomItem }}
      labelText="Select with custom item render"
      items={[
        {
          id: 0,
          name: 'red',
          representation: 'Red',
          hex: '#FF0000',
          description: 'The color of roses',
        },
        {
          id: 1,
          name: 'blue',
          representation: 'Blue',
          hex: '#0000FF',
          description: 'The color of blueberries',
        },
        {
          id: 2,
          name: 'green',
          representation: 'Green',
          hex: '#008000',
          description: 'The color of grass',
        },
      ]}
      itemToString={itemToString}
    />
  );
}
`}</code></pre>
      <h2>{`Custom Components`}</h2>
      <p>{`Out of the box, the `}<inlineCode parentName="p">{`Select`}</inlineCode>{` component uses React Magma-styled iconography for the button used to clear the selection and the caret for the dropdown.
However, these can be overridden by providing custom components to the `}<inlineCode parentName="p">{`components`}</inlineCode>{` prop, in an object containing a `}<inlineCode parentName="p">{`ClearIndicator`}</inlineCode>{` and a `}<inlineCode parentName="p">{`DropdownIndicator`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';

import { Select } from 'react-magma-dom';

export function Example() {
  return (
    <Select
      id="customComponentsId"
      labelText="Custom components"
      items={[
        { label: 'Red', value: 'red' },
        { label: 'Blue', value: 'blue' },
        { label: 'Green', value: 'green' },
      ]}
      initialSelectedItem={{ label: 'Green', value: 'green' }}
      isClearable
      components={{
        ClearIndicator: props => <button onClick={props.onClick}>Clear</button>,
        DropdownIndicator: () => (
          <span style={{ border: '1px solid' }}>Dropdown</span>
        ),
      }}
    />
  );
}
`}</code></pre>
      <h2>{`State Change Types`}</h2>
      <p>{`The `}<inlineCode parentName="p">{`type`}</inlineCode>{` property in the `}<inlineCode parentName="p">{`changes`}</inlineCode>{` object that is returned from the `}<inlineCode parentName="p">{`onStateChange`}</inlineCode>{` function
corresponds to a `}<inlineCode parentName="p">{`stateChangeTypes`}</inlineCode>{` property. The list of all the possible types for a `}<inlineCode parentName="p">{`select`}</inlineCode>{` or a
`}<inlineCode parentName="p">{`multi-select`}</inlineCode>{` are listed below.`}</p>
      <Alert variant="info" style={{
        maxWidth: '100%'
      }} mdxType="Alert">
  In the development environment these types equate to strings
  <br />
  (eg:{' '}
  <inlineCode className="inverse">
    ComboboxStateChangeTypes.InputKeyDownArrowDown =
    '__input_keydown_arrow_down__'
  </inlineCode>
  ). However, in the production environment the types equate to numbers
  <br />
  (eg:{' '}
  <inlineCode className="inverse">
    ComboboxStateChangeTypes.InputKeyDownArrowDown = 0
  </inlineCode>
  ).
      </Alert>
      <h3>{`Select`}</h3>
      <div>
  <ul>
    <li>
      <inlineCode>SelectStateChangeTypes.MenuKeyDownArrowDown</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.MenuKeyDownArrowUp</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.MenuKeyDownEscape</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.MenuKeyDownHome</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.MenuKeyDownEnd</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.MenuKeyDownEnter</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.MenuKeyDownSpaceButton</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.MenuKeyDownCharacter</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.MenuBlur</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.MenuMouseLeave</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.ItemMouseMove</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.ItemClick</inlineCode>
    </li>
    <li>
      <inlineCode>
        SelectStateChangeTypes.ToggleButtonKeyDownCharacter
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        SelectStateChangeTypes.ToggleButtonKeyDownArrowDown
      </inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.ToggleButtonKeyDownArrowUp</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.ToggleButtonClick</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.FunctionToggleMenu</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.FunctionOpenMenu</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.FunctionCloseMenu</inlineCode>
    </li>
    <li>
      <inlineCode>
        SelectStateChangeTypes.FunctionSetHighlightedIndex
      </inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.FunctionSelectItem</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.FunctionSetInputValue</inlineCode>
    </li>
    <li>
      <inlineCode>SelectStateChangeTypes.FunctionReset</inlineCode>
    </li>
  </ul>
      </div>
      <h3>{`MultiSelect`}</h3>
      <div>
  <ul>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.SelectedItemClick
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.SelectedItemKeyDownDelete
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.SelectedItemKeyDownBackspace
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.SelectedItemKeyDownNavigationNext
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.SelectedItemKeyDownNavigationPrevious
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.DropdownKeyDownNavigationPrevious
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.DropdownKeyDownBackspace
      </inlineCode>
    </li>
    <li>
      <inlineCode>MultipleSelectionStateChangeTypes.DropdownClick</inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.FunctionAddSelectedItem
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.FunctionRemoveSelectedItem
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.FunctionSetSelectedItems
      </inlineCode>
    </li>
    <li>
      <inlineCode>
        MultipleSelectionStateChangeTypes.FunctionSetActiveIndex
      </inlineCode>
    </li>
    <li>
      <inlineCode>MultipleSelectionStateChangeTypes.FunctionReset</inlineCode>
    </li>
  </ul>
      </div>
      <h2>{`Select Props`}</h2>
      <SelectProps mdxType="SelectProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      